import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BadgeCheck, ShieldCheck } from 'lucide-react';
import { api, ApiError, apiData } from '@/lib/api';
import { formatPhoneInput } from '@/lib/format';
import { Button, Field, Input } from '@/components/ui';
import { useAuth } from '@/store/auth';
import { AuthLayout } from './AuthLayout';

interface OtpChallenge {
  challengeId: string;
  expiresAt: string;
  /** 개발 환경에서만 내려온다 (§5.1) */
  devCode?: string;
}

interface IdentityAvailability {
  available: boolean;
  storeId: string | null;
  channelKey: string | null;
}

interface IdentityCompleteResult {
  verified: boolean;
  verificationTicket: string;
  phone: string;
  name: string;
  birthDate: string | null;
}

declare global {
  interface Window {
    PortOne?: {
      requestIdentityVerification(
        options: Record<string, unknown>,
      ): Promise<{ code?: string; message?: string } | undefined>;
    };
  }
}

// 포트원 브라우저 SDK — 본인인증 버튼을 누를 때만 내려받는다.
let portoneSdkPromise: Promise<NonNullable<Window['PortOne']>> | null = null;
function loadPortOneSdk(): Promise<NonNullable<Window['PortOne']>> {
  if (window.PortOne) return Promise.resolve(window.PortOne);
  portoneSdkPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.portone.io/v2/browser-sdk.js';
    script.onload = () => {
      if (window.PortOne) resolve(window.PortOne);
      else reject(new Error('PortOne SDK unavailable'));
    };
    script.onerror = () => {
      portoneSdkPromise = null;
      reject(new Error('PortOne SDK load failed'));
    };
    document.head.appendChild(script);
  });
  return portoneSdkPromise;
}

/**
 * 가입 — 명세서 §5.1
 * 이메일+비밀번호 가입 + 휴대폰 SMS 인증(OTP)이 필수다.
 * 포트원 키가 설정된 배포에서는 휴대폰 본인인증(§14.1-6)으로도 시작할 수 있다.
 * 어느 쪽이든 단기 티켓을 받아 가입 요청에 함께 보낸다.
 */
export function RegisterPage() {
  const register = useAuth((s) => s.register);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [step, setStep] = useState<'phone' | 'code' | 'account'>('phone');
  const [phone, setPhone] = useState('');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [code, setCode] = useState('');
  const [ticket, setTicket] = useState<string | null>(null);
  /** 본인인증으로 확인된 이름 — 채워지면 이름 입력을 잠근다 */
  const [identityName, setIdentityName] = useState<string | null>(null);

  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    // 본인인증 리다이렉트 복귀 시에는 code가 포트원 오류 코드일 수 있어 invite를 따로 쓴다.
    inviteCode:
      params.get('invite') ??
      (params.get('identityVerificationId') ? '' : (params.get('code') ?? '')),
  });

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // 포트원 본인인증 (§14.1-6) — 키가 설정된 배포에서만 버튼이 나온다.
  const { data: identity } = useQuery({
    queryKey: ['auth', 'identity-verification'],
    queryFn: () => apiData<IdentityAvailability>('/auth/identity-verification'),
    staleTime: Infinity,
    retry: false,
  });

  async function run(fn: () => Promise<void>) {
    setError(null);
    setLoading(true);
    try {
      await fn();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : '연결이 잠시 불안정해요. 다시 시도해 주세요.',
      );
    } finally {
      setLoading(false);
    }
  }

  async function completeIdentity(identityVerificationId: string) {
    const { data } = await api<IdentityCompleteResult>('/auth/identity-verification/complete', {
      method: 'POST',
      body: { identityVerificationId },
    });
    setTicket(data.verificationTicket);
    setPhone(data.phone);
    setIdentityName(data.name);
    setForm((f) => ({ ...f, name: data.name }));
    setStep('account');
  }

  // 모바일에서는 인증 창이 리다이렉트로 돌아온다 — StrictMode 중복 실행을 ref로 막는다.
  const returnedId = params.get('identityVerificationId');
  const completing = useRef(false);
  useEffect(() => {
    if (!returnedId || completing.current) return;
    completing.current = true;
    void run(() => completeIdentity(returnedId)).finally(() => {
      completing.current = false;
      const next = new URLSearchParams();
      const invite = params.get('invite');
      if (invite) next.set('invite', invite);
      setParams(next, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnedId]);

  function startIdentity() {
    if (!identity?.available || !identity.storeId || !identity.channelKey) return;
    void run(async () => {
      const sdk = await loadPortOneSdk().catch(() => {
        throw new ApiError('SDK', '인증 창을 여는 데 실패했어요. 잠시 후 다시 시도해 주세요.', 0);
      });
      const identityVerificationId = `identity-verification-${crypto.randomUUID()}`;
      const invite = form.inviteCode.trim();
      const redirectUrl = `${window.location.origin}/register${
        invite ? `?invite=${encodeURIComponent(invite)}` : ''
      }`;
      const response = await sdk.requestIdentityVerification({
        storeId: identity.storeId!,
        identityVerificationId,
        channelKey: identity.channelKey!,
        redirectUrl,
        // KG이니시스 통합인증 필수 파라미터 — 고정 사용자 아님
        bypass: { inicisUnified: { flgFixedUser: 'N' } },
      });
      // code가 있으면 취소·실패다. 리다이렉트 방식에서는 여기 도달하지 않는다.
      if (response?.code !== undefined) {
        throw new ApiError('CANCELLED', response.message ?? '본인인증이 취소되었어요.', 0);
      }
      await completeIdentity(identityVerificationId);
    });
  }

  const requestOtp = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const { data } = await api<OtpChallenge>('/auth/phone/request-otp', {
        method: 'POST',
        body: { phone: phone.trim(), purpose: 'SIGNUP' },
      });
      setChallenge(data);
      if (data.devCode) setCode(data.devCode);
      setStep('code');
    });
  };

  const verifyOtp = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const { data } = await api<{ verificationTicket: string }>('/auth/phone/verify', {
        method: 'POST',
        body: { challengeId: challenge?.challengeId, code: code.trim() },
      });
      setTicket(data.verificationTicket);
      setStep('account');
    });
  };

  const submitAccount = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      await register({
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        verificationTicket: ticket!,
        ...(form.inviteCode.trim() ? { inviteCode: form.inviteCode.trim() } : {}),
      });
      navigate('/', { replace: true });
    });
  };

  return (
    <AuthLayout
      title="새싹돋보기 시작하기"
      description={
        step === 'phone'
          ? '먼저 휴대폰 번호를 확인할게요.'
          : step === 'code'
            ? '문자로 보내 드린 6자리 숫자를 입력해 주세요.'
            : '마지막이에요. 계정 정보를 적어 주세요.'
      }
      footer={
        <>
          이미 계정이 있으신가요?{' '}
          <Link to="/login" className="font-semibold text-primary-strong underline">
            로그인
          </Link>
        </>
      }
    >
      {step === 'phone' && (
        <form onSubmit={requestOtp} className="flex flex-col gap-4">
          <Field label="휴대폰 번호" hint="숫자만 입력해도 괜찮아요." required>
            <Input
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              required
              value={phone}
              onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
              placeholder="01012345678"
            />
          </Field>
          <ErrorText message={error} />
          <Button type="submit" block size="lg" loading={loading}>
            인증번호 받기
          </Button>

          {identity?.available && (
            <>
              <p className="text-center text-xs text-ink-sub">또는</p>
              <Button
                type="button"
                variant="outline"
                block
                size="lg"
                disabled={loading}
                icon={<BadgeCheck size={18} />}
                onClick={startIdentity}
              >
                휴대폰 본인인증으로 시작하기
              </Button>
            </>
          )}
        </form>
      )}

      {step === 'code' && (
        <form onSubmit={verifyOtp} className="flex flex-col gap-4">
          <Field label="인증번호" hint="3분 안에 입력해 주세요." required>
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="tnum tracking-[0.3em]"
            />
          </Field>
          {challenge?.devCode && (
            <p className="rounded-sm bg-accent-soft px-3 py-2 text-sm text-ink">
              개발 환경이라 인증번호를 바로 보여드려요: <b className="tnum">{challenge.devCode}</b>
            </p>
          )}
          <ErrorText message={error} />
          <Button type="submit" block size="lg" loading={loading}>
            확인
          </Button>
          <button
            type="button"
            className="text-sm text-ink-sub underline"
            onClick={() => setStep('phone')}
          >
            번호를 다시 입력할게요
          </button>
        </form>
      )}

      {step === 'account' && (
        <form onSubmit={submitAccount} className="flex flex-col gap-4">
          <p className="flex items-center gap-2 rounded-sm bg-primary-soft px-3 py-2 text-sm text-primary-strong">
            <ShieldCheck size={16} />{' '}
            {identityName ? '휴대폰 본인인증이 끝났어요.' : '휴대폰 인증이 끝났어요.'}
          </p>

          <Field
            label="이름"
            hint={identityName ? '본인인증으로 확인한 이름이에요.' : undefined}
            required
          >
            <Input
              required
              autoComplete="name"
              value={form.name}
              disabled={identityName !== null}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="정보호"
            />
          </Field>

          <Field label="이메일" required>
            <Input
              type="email"
              required
              autoComplete="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>

          <Field label="비밀번호" hint="8자 이상으로 정해 주세요." required>
            <Input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </Field>

          <Field label="초대코드" hint="원에서 받으신 코드가 있다면 함께 넣어 주세요.">
            <Input
              value={form.inviteCode}
              onChange={(e) => setForm({ ...form, inviteCode: e.target.value.toUpperCase() })}
              placeholder="ABCD1234"
              className="tracking-widest"
            />
          </Field>

          <ErrorText message={error} />
          <Button type="submit" block size="lg" loading={loading}>
            가입 완료
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

function ErrorText({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
      {message}
    </p>
  );
}
